import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useQueryStates } from 'nuqs';
import { SidebarMenuButton } from '@clay-run/glaze/sidebar-next/index';
import { Avatar, getAvatarInitials } from '@clay-run/glaze/avatar';
import {
  Dropdown,
  DropdownButtonUnstyled,
  DropdownHeader,
  DropdownItem,
  DropdownItemCheckbox,
  DropdownMenu,
  DropdownSeparator,
} from '@clay-run/glaze/dropdown/index';
import { Icon } from '@clay-run/glaze/icon';
import { Text } from '@clay-run/glaze/text';
import { FLOW_ICONS } from '../icons';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { useSetTerraDesignV2, useTerraDesignV2Enabled } from '../../../hooks/useTerraDesignV2';
import { openImpersonationPalette } from '../../../lib/impersonation-palette-store';
import { setTheme, useThemeSync } from '../../../lib/theme';
import { modalParams } from '../../../lib/urlParams';
import { emailToDisplayName } from '../../../view-system/utils/displayFormatters';

/**
 * The rail's avatar trigger and its account menu. Renders the `Dropdown`
 * only. The rail supplies the `SidebarMenuItem` around it.
 *
 * The trigger IS a `SidebarMenuButton`, so it has the same hover fill, box
 * size, radius and tooltip as the rail items above it. `DropdownButtonUnstyled`
 * supplies the dropdown's trigger ref and props through Base UI's `render`
 * prop, so one `<button>` carries both. The avatar sits in a 16px box with
 * the size and centring of the icon slot, so it lines up with the icons above
 * it. The box lets the impersonation ring draw outside it. The rail is the
 * icon strip, so the trigger shows the avatar alone and the rep's name is its
 * tooltip.
 *
 * Menu, top to bottom: the name and email, Switch user, Admin, Preferences,
 * "Terra Beta" and "Dark theme", Log out.
 *
 * There is no User Role row here. The rail-footer chip (`UiViewChip`) is the one
 * role switcher in this shell, so the avatar menu carries no second copy.
 *
 * Glaze's dropdown has no radio-item primitive, so the theme choice is one
 * `menuitemcheckbox` ("Dark theme", checked = dark).
 */
export default function RailAccountMenu() {
  const navigate = useNavigate();
  const { isAdmin, canImpersonate, email } = useUserControlsContext();
  const { active: isImpersonating } = useImpersonation();
  const { signOut } = useClerk();
  const [, setModalParams] = useQueryStates(modalParams);
  const { enabled: designV2Enabled } = useTerraDesignV2Enabled();
  const { setEnabled: setDesignV2Enabled } = useSetTerraDesignV2();
  const theme = useThemeSync();

  const displayName = emailToDisplayName(email);
  const initials = getAvatarInitials(displayName, '?');
  // emailToDisplayName falls back to 'User', which the tooltip should not
  // show when there is no email to derive a name from.
  const accountLabel = email ? displayName : 'Account';

  return (
    <Dropdown placement="top-start">
      <DropdownButtonUnstyled
        aria-label={`${displayName} — account menu${isImpersonating ? ' (impersonating)' : ''}`}
        render={(triggerProps) => (
          <SidebarMenuButton {...triggerProps} tooltip={accountLabel}>
            {triggerProps.children}
          </SidebarMenuButton>
        )}
      >
        <span className="flex size-4 min-w-4 max-w-4 shrink-0 items-center justify-center overflow-visible!">
          <Avatar
            size="xs"
            fallbackString={initials}
            className={isImpersonating ? 'ring-2 ring-border-warning ring-offset-1 ring-offset-bg-primary' : undefined}
          />
        </span>
      </DropdownButtonUnstyled>
      <DropdownMenu>
        <DropdownHeader>
          <Text size="sm" weight="medium" variant="primary" className="truncate">{displayName}</Text>
          {email ? <Text size="xs" variant="tertiary" className="truncate">{email}</Text> : null}
        </DropdownHeader>
        <DropdownSeparator />
        {canImpersonate && (
          <DropdownItem onClick={() => openImpersonationPalette()}>
            <Icon type={FLOW_ICONS.switchUser} />
            Switch user…
          </DropdownItem>
        )}
        {(isAdmin || canImpersonate) && (
          <DropdownItem onClick={() => navigate('/admin')}>
            <Icon type={FLOW_ICONS.admin} />
            Admin
          </DropdownItem>
        )}
        <DropdownItem onClick={() => { void setModalParams({ modal: 'preferences' }); }}>
          <Icon type={FLOW_ICONS.settings} />
          Preferences
        </DropdownItem>
        <DropdownSeparator />
        <DropdownItemCheckbox
          keepOpenAfterClick
          checked={designV2Enabled}
          onChange={(next) => setDesignV2Enabled(next)}
        >
          <Icon type={FLOW_ICONS.designBeta} />
          Terra Beta
        </DropdownItemCheckbox>
        <DropdownItemCheckbox
          keepOpenAfterClick
          checked={theme === 'dark'}
          onChange={(next) => setTheme(next ? 'dark' : 'light')}
        >
          <Icon type={FLOW_ICONS.themeDark} />
          Dark theme
        </DropdownItemCheckbox>
        <DropdownSeparator />
        <DropdownItem onClick={() => { void signOut(); }}>
          <Icon type={FLOW_ICONS.logOut} />
          Log out
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
